import React, { forwardRef, useImperativeHandle } from 'react';
import { TestReportProps } from '../../../types/certificate.ts';
import { useTestReport } from '../hooks/useTestReport.ts';
import styles from './TestReport.module.css';

// 定义组件暴露的方法接口
interface TestReportHandle {
  generatePDF: () => Promise<any>;
  downloadPDF: (filename?: string) => Promise<void>;
  isGenerating: boolean;
}

/**
 * 产品检测报告组件
 * 严格按照模板1:1还原设计
 */
export const TestReport = forwardRef<TestReportHandle, TestReportProps>(({
  reportData,
  className = ''
}, ref) => {
  const { reportRef, reportId, reportUrl, generatePDF, downloadPDF, isGenerating } = useTestReport();

  // 通过ref暴露方法给父组件
  useImperativeHandle(ref, () => ({
    generatePDF,
    downloadPDF,
    isGenerating
  }), [generatePDF, downloadPDF, isGenerating]);

  return (
    <div className={`${styles.testReportContainer} ${className}`}>
      <div ref={reportRef} className={styles.testReport}>
        {/* 页眉 - Logo和标题 */}
        <div className={styles.header}>
          <div style={{ width: "120px" }}>{/* 左侧空白平衡布局 */}</div>
          <div className={styles.title}>
            <h1 className={styles.titleEn}>CERTIFICATE OF ANALYSIS</h1>
            <h2 className={styles.titleCn}>产品检测报告</h2>
          </div>

          <div>
            <img src="/logo.png" alt="Jotun Logo" className={styles.logo} />
          </div>
        </div>

        {/* 产品信息部分 */}
        <div className={styles.productInfo}>
          <div className={styles.infoRow}>
            <div className={styles.infoItem}>
              <span className={styles.labelEn}>
                Customer Order
                <span className={styles.labelCn}>(订单号)</span>
              </span>

              <span className={styles.valueEn}>{reportData.orderNumber}</span>
            </div>
            <div className={styles.quantityItem}>
              <span className={styles.labelEn}>Quantity</span>
              <span className={styles.labelCn} style={{ marginRight: "20px" }}>
                (桶数):
              </span>
              <span className={styles.valueEn}>{reportData.quantity}</span>
            </div>
          </div>

          <div className={styles.infoRow}>
            <div className={styles.infoItem}>
              <span className={styles.labelEn}>PRODUCT</span>
              <span className={styles.valueEn}>
                STEELMASTER 60WB WHITE 18.5L
              </span>
            </div>
          </div>

          <div className={styles.infoRow}>
            <div className={styles.infoItem}>
              <span className={styles.labelCn} style={{ width: "230px" }}>
                产品名称
              </span>
              <span className={styles.valueCn} style={{ marginLeft: "12px" }}>
                <span className={styles.valueEn}>
                  {" "}
                  GT-NSP-Fp2.00-Steelmaster 60WB
                </span>
                室内膨胀型钢结构防火涂料白色
              </span>
            </div>
          </div>

          <div className={styles.infoRow}>
            <div className={styles.infoItem}>
              <span className={styles.labelEn}>
                PROD.DATE
                <span className={styles.labelCn}>( 生产日期 )</span>
              </span>
              <span className={styles.valueEn}>
                {reportData.productionDate}
              </span>
            </div>
          </div>

          <div className={styles.infoRow}>
            <div className={styles.infoItem}>
              <span className={styles.labelEn}>
                Durability Days
                <span className={styles.labelCn}>(保质期,天)</span>
              </span>

              <span className={styles.valueEn}>183</span>
            </div>
          </div>

          <div className={styles.infoRow}>
            <div className={styles.infoItem}>
              <span className={styles.labelEn}>
                BATCH NO.
                <span className={styles.labelCn}>( 产品批号 )</span>
              </span>

              <span className={styles.valueEn}>{reportData.batchNumber}</span>
            </div>
          </div>

          <div className={styles.infoRow}>
            <div className={styles.infoItem}>
              <span className={styles.labelEn}>
                Product Code
                <span className={styles.labelCn}>( 产品编码 )</span>
              </span>

              <span className={styles.valueEn}>0SM001UQA</span>
            </div>
          </div>
        </div>

        {/* 检测表格 */}
        <table className={styles.testTable} style={{ marginRight: "6px" }}>
          <thead>
            <tr>
              <th
                className={styles.testHeader}
                rowSpan={2}
                style={{ width: "235px" }}
              >
                <span className={styles.englishPart}>TESTS</span>
                <span className={styles.chineseText}>(项 目)/</span>
                <br />
                <span className={styles.englishPart}>REF. STANDARD</span>
                <span className={styles.chineseText}>(测试标准)</span>
              </th>
              <th className={styles.specHeader} style={{ width: "230px" }}>
                <span className={styles.englishPart}>SPECIFICATION*</span>
                <br />
                <span className={styles.chineseText}>指标</span>
              </th>
              <th className={styles.resultHeader} rowSpan={2}>
                <span className={styles.englishPart}>RESULT</span>
                <br />
                <span className={styles.chineseText}>结果</span>
              </th>
              <th className={styles.unitHeader} rowSpan={2}>
                <span className={styles.englishPart}>UOM</span>
                <span className={styles.chineseText}>/单位</span>
              </th>
            </tr>
            <tr>
              <th className={styles.minMaxSubHeader}>Min.- Max.</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className={styles.testCell}>
                <span className={styles.chineseText}>在容器中状态/</span>{" "}
                <span className={styles.englishPart}>GB 14907-2018</span>
              </td>
              <td className={styles.specCell}>
                <span className={styles.chineseText}>
                  经搅拌后呈均匀细腻状态或稠
                  <br />
                  厚流体状态，无结块
                </span>
              </td>
              <td className={styles.resultCell}>
                <span className={styles.chineseText}>合格</span>
              </td>
              <td className={styles.unitCell}>*</td>
            </tr>
            <tr>
              <td className={styles.testCell}>
                <span className={styles.chineseText}>干燥时间(表干)/</span>{" "}
                <span className={styles.englishPart}>GB/T 1728-2020</span>
              </td>
              <td className={styles.specCell}>
                ≤<span className={styles.englishPart}>12</span>
              </td>
              <td className={styles.resultCell}>
                <span className={styles.englishPart}>7</span>
              </td>
              <td className={styles.unitCell}>h</td>
            </tr>
            <tr>
              <td className={styles.testCell}>
                <span className={styles.chineseText}>初期干燥抗裂性/</span>{" "}
                <span className={styles.englishPart}>GB/T 9779-2015</span>
              </td>
              <td className={styles.specCell}>
                <span className={styles.chineseText}>不应出现裂纹</span>
              </td>
              <td className={styles.resultCell}>
                <span className={styles.chineseText}>合格</span>
              </td>
              <td className={styles.unitCell}>*</td>
            </tr>
            <tr>
              <td className={styles.testCell}>
                <span className={styles.chineseText}>
                  隔热效率偏差(抽检项)/
                </span>{" "}
                <span className={styles.englishPart}>
                  GB
                  <br />
                  14907-2018
                </span>
              </td>
              <td className={styles.specCell}>
                <span className={styles.englishPart}>-15-15</span>
              </td>
              <td className={styles.resultCell}>
                <span className={styles.chineseText}>合格</span>
              </td>
              <td className={styles.unitCell}>%</td>
            </tr>
            <tr>
              <td className={styles.testCell}>
                <span className={styles.chineseText}>耐水性(抽检项)/</span>{" "}
                <span className={styles.englishPart}>GB 14907-2018</span>
              </td>
              <td className={styles.specCell}>
                <span className={styles.englishPart}>24h</span>
                <span className={styles.chineseText}>
                  试验后,涂层应无起层、发
                  <br />
                  泡、脱落现象，且隔热效率衰
                  <br />
                  减量应
                </span>
                ≤<span className={styles.englishPart}>35%</span>
              </td>
              <td className={styles.resultCell}>
                <span className={styles.chineseText}>合格</span>
              </td>
              <td className={styles.unitCell}>*</td>
            </tr>
            <tr>
              <td className={styles.testCell}>
                <span className={styles.englishPart}>PH</span>
                <span className={styles.chineseText}>值</span>
              </td>
              <td className={styles.specCell}>7.5-8.5</td>
              <td className={styles.resultCell}>8</td>
              <td className={styles.unitCell}>*</td>
            </tr>
          </tbody>
        </table>

        {/* 说明文字 */}
        <div className={styles.note}>
          <p>
            *The specification is given by the formulation
            responsible/laboratory
          </p>
          <p className={styles.chineseText}>以上指标由我司实验室提供</p>
        </div>

        {/* 页脚信息 */}
        <div className={styles.footer}>
          <div className={styles.footerLeft}>
            <p>
              <span className={styles.englishText}>Place and date:</span>
              <span className={styles.chineseText}>生成日期</span>
            </p>
            <p className={styles.englishPart}>{reportData.generationDate}</p>
          </div>
          <div className={styles.footerRight}>
            <div className={styles.stampContainer}>
              <img
                src="/seal.png"
                alt="检测印章"
                className={styles.stampImage}
              />
              <div className={styles.stampText}>
                Bruce Huang
                <br />
                QC-lab./Lab.Manager
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

// 设置显示名称
TestReport.displayName = 'TestReport';
